import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';
import type { ApiError } from '@/lib/api/error';
import { OrderStatusBanner } from '@/routes/_staff/devices/$id/_layout/-shared/-components/OrderStatusBanner';

import {
  DeviceDetailFormStore,
  useDeviceDetailFormStore,
  useGetDeviceProfile,
  useGetDeviceProfileHeader,
  useSaveDeviceProfile,
  DeviceDetailSectionForm,
} from '../-shared';

export const Route = createFileRoute('/_staff/devices/$id/_layout/profile/')({
  component: DeviceProfileTab,
});

export function DeviceProfileTab() {
  const { id } = useParams({ from: '/_staff/devices/$id/_layout' });
  const store = DeviceDetailFormStore.createStore();
  return useMemo(
    () => (
      <DeviceDetailFormStore.Provider value={store}>
        <DeviceProfileContent id={id} />
      </DeviceDetailFormStore.Provider>
    ),
    [store, id],
  );
}

function DeviceProfileContent({ id }: { id: string }) {
  const { data, isPending } = useGetDeviceProfile(id);
  const { data: headerData } = useGetDeviceProfileHeader(id);
  const { actions } = useDeviceDetailFormStore();
  const saveMutation = useSaveDeviceProfile(id, {
    onError: (error: ApiError) => actions.onApiError(error),
  });

  if (!data) {
    return <Loader />;
  }

  return (
    <VStack className="h-full">
      {headerData &&
        headerData.status === 'IN_DISTRIBUTION' &&
        headerData.marketplaceOrderId && (
          <OrderStatusBanner
            checkOrderLinkUrl={`/marketplace/sent-orders/${headerData.marketplaceOrderId}`}
          />
        )}
      <DeviceDetailSectionForm
        data={data}
        isPending={isPending}
        readOnly={headerData?.status === 'DECOMMISSIONED'}
        saveMutateAsync={saveMutation.mutateAsync}
      />
    </VStack>
  );
}
